import { BaseApi } from '@shared/config';
import {
  type SignInRequest,
  type SignInResponse,
  type User,
  type RecoveryPasswordRequest,
  type RecoveryPasswordResponse,
  type ResetPasswordRequest,
  type ResetPasswordResponse,
  type RegisterRequest,
  type RegisterResponse,
  type SurferRegisterRequest,
  type SurferRegisterResponse,
  type SurferRegistrationPreflightRequest,
  type SurferRegistrationResendRequest,
  type VerifiedSurferRegistrationRequest,
  type RegistrationPreflightRequest,
  type RegistrationResendRequest,
  type RegistrationVerification,
  type VerifiedRegistrationRequest,
  type VerifyEmailRequest,
  type VerifyEmailResponse,
} from './types';

class AuthApi extends BaseApi {
  private readonly basePrefix = 'authentication';

  async signIn(data: SignInRequest): Promise<SignInResponse> {
    return this.instance.post(`${this.basePrefix}/authenticate`, data);
  }

  async getMe(): Promise<User> {
    return this.instance.get(`${this.basePrefix}/me`);
  }

  async recoveryPassword(
    data: RecoveryPasswordRequest
  ): Promise<RecoveryPasswordResponse> {
    return this.instance.post(
      `${this.basePrefix}/recovery/password/request`,
      data
    );
  }

  async resetPassword(
    data: ResetPasswordRequest
  ): Promise<ResetPasswordResponse> {
    return this.instance.post(
      `${this.basePrefix}/recovery/password/reset`,
      data
    );
  }

  async signOut(): Promise<void> {
    return this.instance.post(`${this.basePrefix}/logout`);
  }

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    return this.instance.post(`${this.basePrefix}/register`, data);
  }

  async prepareRegistration(
    data: RegistrationPreflightRequest
  ): Promise<RegistrationVerification> {
    return this.instance.post(
      `${this.basePrefix}/v2/register/verification`,
      data,
      { handleNetworkErrorLocally: true }
    );
  }

  async resendRegistrationCode(
    data: RegistrationResendRequest
  ): Promise<RegistrationVerification> {
    return this.instance.post(
      `${this.basePrefix}/v2/register/verification/resend`,
      data,
      { handleNetworkErrorLocally: true }
    );
  }

  async registerVerified(
    data: VerifiedRegistrationRequest
  ): Promise<RegisterResponse> {
    return this.instance.post(`${this.basePrefix}/v2/register`, data, {
      handleNetworkErrorLocally: true,
    });
  }

  async surferRegister(
    data: SurferRegisterRequest
  ): Promise<SurferRegisterResponse> {
    return this.instance.post(`${this.basePrefix}/surfer-register`, data);
  }

  async prepareSurferRegistration(
    data: SurferRegistrationPreflightRequest
  ): Promise<RegistrationVerification> {
    return this.instance.post(
      `${this.basePrefix}/v2/surfer-register/verification`,
      data,
      { handleNetworkErrorLocally: true }
    );
  }

  async resendSurferRegistrationCode(
    data: SurferRegistrationResendRequest
  ): Promise<RegistrationVerification> {
    return this.instance.post(
      `${this.basePrefix}/v2/surfer-register/verification/resend`,
      data,
      { handleNetworkErrorLocally: true }
    );
  }

  async surferRegisterVerified(
    data: VerifiedSurferRegistrationRequest
  ): Promise<SurferRegisterResponse> {
    return this.instance.post(`${this.basePrefix}/v2/surfer-register`, data, {
      handleNetworkErrorLocally: true,
    });
  }

  async verifyEmail(data: VerifyEmailRequest): Promise<VerifyEmailResponse> {
    return this.instance.post(`${this.basePrefix}/verify`, data);
  }
}

export const authApi = new AuthApi();
